import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { FormControlLabel, Checkbox } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const ReportGeneralDataComponent = ({ reportItem, onPropertyChanged, openModes, reportTypes, validation, partnerTypes, documentTypes }) => {
  const [t] = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          isNumeric
          fullWidth
          decimalScale={0}
          value={reportItem.orderNumber}
          onChange={event => {
            if (event !== reportItem.orderNumber) onPropertyChanged('orderNumber', event);
          }}
          label={t('Reports.OrderNo')}
          error={!isValid(validation.orderNumber)}
          helperText={getErrors(validation.orderNumber)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={reportItem.reportName || ''}
          onChange={event => onPropertyChanged('reportName', event)}
          label={t('Reports.ReportName')}
          error={!isValid(validation.reportName)}
          helperText={getErrors(validation.reportName)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={reportItem.reportFriendlyName || ''}
          onChange={event => onPropertyChanged('reportFriendlyName', event)}
          label={t('Reports.ReportFriendlyName')}
          error={!isValid(validation.reportFriendlyName)}
          helperText={getErrors(validation.reportFriendlyName)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          labelKey='value'
          valueKey='value'
          value={{ value: reportItem.reportType }}
          options={reportTypes}
          onChange={event => onPropertyChanged('reportType', event !== null ? event.value : null)}
          label={t('Reports.ReportType')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          labelKey='value'
          valueKey='value'
          value={{ value: reportItem.openMode }}
          options={openModes}
          onChange={event => onPropertyChanged('openMode', event !== null ? event.value : null)}
          label={t('Reports.OpenMode')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={reportItem.storedProcedureName || ''}
          onChange={event => onPropertyChanged('storedProcedureName', event)}
          label={t('Reports.StoredProcedureName')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={reportItem.countStoredProcedureName || ''}
          onChange={event => onPropertyChanged('countStoredProcedureName', event)}
          label={t('Reports.CountStoredProcedureName')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(reportItem.showInReportList) || false}
              onChange={event => onPropertyChanged('showInReportList', event.target.checked)}
              color='primary'
            />
          }
          label={t('Reports.ShowInReportList')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(reportItem.showInPartner) || false}
              onChange={event => onPropertyChanged('showInPartner', event.target.checked)}
              color='primary'
            />
          }
          label={t('Reports.ShowInPartner')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(reportItem.showInDocument) || false}
              onChange={event => onPropertyChanged('showInDocument', event.target.checked)}
              color='primary'
            />
          }
          label={t('Reports.ShowInDocument')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(reportItem.isSensitiveInfo) || false}
              onChange={event => onPropertyChanged('isSensitiveInfo', event.target.checked)}
              color='primary'
            />
          }
          label={t('Reports.IsSensitiveInfo')}
        />
      </Grid>

      {reportItem.showInPartner && (
        <Grid item xs={12} sm={12}>
          <Autocomplete
            isMultiSelection={true}
            simpleValue={true}
            isClearable={true}
            fullWidth
            label={t('Reports.PartnerTypes')}
            isSearchable={true}
            options={partnerTypes}
            onChange={event => onPropertyChanged('partnerTypeIds', event)}
            value={reportItem.reportXPartnerTypeIds}
            error={!isValid(validation.reportXPartnerTypes)}
            helperText={getErrors(validation.reportXPartnerTypes)}
          />
        </Grid>
      )}

      {reportItem.showInDocument && (
        <Grid item xs={12} sm={12}>
          <Autocomplete
            isMultiSelection={true}
            simpleValue={true}
            isClearable={true}
            fullWidth
            label={t('Reports.DocumentTypes')}
            isSearchable={true}
            options={documentTypes}
            onChange={event => onPropertyChanged('documentTypeIds', event)}
            value={reportItem.reportXDocumentTypeIds}
            error={!isValid(validation.reportXDocumentTypes)}
            helperText={getErrors(validation.reportXDocumentTypes)}
          />
        </Grid>
      )}
    </Grid>
  );
};

ReportGeneralDataComponent.propTypes = {
  reportItem: PropTypes.object.isRequired,
  openModes: PropTypes.array.isRequired,
  reportTypes: PropTypes.array.isRequired,
  onPropertyChanged: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  partnerTypes: PropTypes.array,
  documentTypes: PropTypes.array
};

export default ReportGeneralDataComponent;
